import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowUpDown, ChevronDown, BarChart3, Clock, CheckCircle2, AlertTriangle, Search, Filter } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUT_OPTIONS = [
    { value: 'en_attente', label: 'En attente', dot: 'bg-amber-500', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700' },
    { value: 'reserve', label: 'Reserve', dot: 'bg-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700' },
];

export default function MonteMeuble() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDrop, setOpenDrop] = useState(null);
    const [search, setSearch] = useState('');
    const [statutFilter, setStatutFilter] = useState('all');

    const fetchItems = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/monte-meuble-list`, { withCredentials: true });
            const sorted = [...res.data].sort((a, b) => {
                if ((b.is_urgent ? 1 : 0) !== (a.is_urgent ? 1 : 0)) return (b.is_urgent ? 1 : 0) - (a.is_urgent ? 1 : 0);
                const da = a.date ? new Date(a.date) : new Date('9999-12-31');
                const db2 = b.date ? new Date(b.date) : new Date('9999-12-31');
                return da - db2;
            });
            setItems(sorted);
        } catch {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchItems(); }, []);

    const handleStatutChange = async (clientId, type, newStatut) => {
        try {
            // Day-based item ("jour_N"): route to /jour/statut endpoint with jour_index.
            // Legacy item ("depart"/"arrivee"): route to /statut endpoint with type.
            if (typeof type === 'string' && type.startsWith('jour_')) {
                const jourIndex = parseInt(type.split('_')[1], 10);
                await axios.post(`${API_URL}/api/clients/${clientId}/monte-meuble/jour/statut`,
                    { jour_index: jourIndex, statut: newStatut },
                    { withCredentials: true });
            } else {
                await axios.post(`${API_URL}/api/clients/${clientId}/monte-meuble/statut`,
                    { type, statut: newStatut },
                    { withCredentials: true });
            }
            toast.success('Statut mis a jour');
            fetchItems();
        } catch {
            toast.error('Erreur');
        }
    };

    const stats = useMemo(() => ({
        total: items.length,
        en_attente: items.filter(i => (i.statut || 'en_attente') === 'en_attente').length,
        reserve: items.filter(i => i.statut === 'reserve').length,
        urgent: items.filter(i => i.is_urgent).length,
    }), [items]);

    const filteredItems = useMemo(() => {
        return items.filter((it) => {
            if (statutFilter === 'urgent' && !it.is_urgent) return false;
            if (statutFilter !== 'all' && statutFilter !== 'urgent' && (it.statut || 'en_attente') !== statutFilter) return false;
            if (search.trim()) {
                const q = search.trim().toLowerCase();
                const hay = `${it.client_nom || ''} ${it.adresse || ''}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [items, search, statutFilter]);

    const StatCard = ({ icon: Icon, label, value, border, iconColor, onClick, active }) => (
        <button
            type="button"
            onClick={onClick}
            className={`bg-white rounded-2xl border-2 ${active ? border.replace('/50', '') : border} p-5 text-left transition-all hover:-translate-y-0.5 hover:shadow-md ${active ? 'shadow-md' : 'shadow-sm'}`}
            data-testid={`mm-stat-${label.toLowerCase().replace(/\s/g, '-')}`}
        >
            <div className="flex items-center gap-2 mb-1">
                <Icon className={`w-5 h-5 ${iconColor}`} />
                <span className="text-3xl font-bold text-[#1B365D]">{value}</span>
            </div>
            <span className="text-sm text-gray-500">{label}</span>
        </button>
    );

    return (
        <Layout>
            <div className="space-y-6" data-testid="monte-meuble-page">
                <div className="flex items-center gap-3">
                    <ArrowUpDown className="w-6 h-6 text-[#1B365D]" />
                    <h1 className="text-2xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Monte-meuble
                    </h1>
                </div>

                {/* Stats cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard icon={BarChart3} label="Total" value={stats.total} border="border-gray-200/70" iconColor="text-gray-500"
                        active={statutFilter === 'all'} onClick={() => setStatutFilter('all')} />
                    <StatCard icon={Clock} label="En attente" value={stats.en_attente} border="border-amber-300/70" iconColor="text-amber-500"
                        active={statutFilter === 'en_attente'} onClick={() => setStatutFilter('en_attente')} />
                    <StatCard icon={CheckCircle2} label="Reserve" value={stats.reserve} border="border-emerald-300/70" iconColor="text-emerald-500"
                        active={statutFilter === 'reserve'} onClick={() => setStatutFilter('reserve')} />
                    <StatCard icon={AlertTriangle} label="Urgent" value={stats.urgent} border="border-rose-300/70" iconColor="text-rose-500"
                        active={statutFilter === 'urgent'} onClick={() => setStatutFilter('urgent')} />
                </div>

                {/* Search + filter */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Rechercher (nom)..."
                            className="pl-9 bg-gray-50 border-gray-200"
                            data-testid="mm-search"
                        />
                    </div>
                    <div className="relative sm:min-w-[180px]">
                        <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <select
                            value={statutFilter}
                            onChange={(e) => setStatutFilter(e.target.value)}
                            className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 appearance-none"
                            data-testid="mm-statut-filter"
                        >
                            <option value="all">Tous les statuts</option>
                            <option value="en_attente">En attente</option>
                            <option value="reserve">Reserve</option>
                            <option value="urgent">Urgent</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                </div>

                <div className="bg-white rounded-xl border-2 border-emerald-300 shadow-sm p-6">
                    {loading ? (
                        <div className="flex items-center justify-center h-32">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1B365D]" />
                        </div>
                    ) : filteredItems.length === 0 ? (
                        <div className="text-center py-12">
                            <ArrowUpDown className="w-12 h-12 text-gray-200 mx-auto mb-4" />
                            <p className="text-gray-400 text-sm">Aucun monte-meuble {statutFilter !== 'all' ? 'correspondant' : 'en cours'}</p>
                            <p className="text-gray-300 text-xs mt-1">{statutFilter === 'all' ? "Les monte-meubles reserves n'apparaissent plus ici" : 'Ajustez votre recherche ou filtre'}</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {filteredItems.map((item, idx) => {
                                const currentStatut = item.statut || 'en_attente';
                                const sCfg = STATUT_OPTIONS.find(o => o.value === currentStatut) || STATUT_OPTIONS[0];
                                const dropKey = `${item.client_id}-${item.type}`;
                                return (
                                    <div key={idx} className="border border-gray-200 rounded-xl p-5 flex items-center justify-between hover:border-gray-300 transition-colors" data-testid={`mm-item-${idx}`}>
                                        <div className="flex items-center gap-4">
                                            <Badge className={`text-xs ${item.type === 'depart' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-red-100 text-red-600 border-red-200'} border`}>
                                                {item.type === 'depart' ? 'Partir' : 'Arriver'}
                                            </Badge>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <button onClick={() => navigate(`/clients/${item.client_id}`)} className="text-sm font-bold text-[#1B365D] hover:underline">
                                                        {item.client_nom}
                                                    </button>
                                                    {item.is_urgent && (
                                                        <Badge className="bg-red-500 text-white text-[9px] px-1.5 py-0.5 font-bold animate-pulse">URGENT</Badge>
                                                    )}
                                                </div>
                                                <p className="text-sm text-gray-600">{item.adresse || '-'}</p>
                                                <p className="text-xs text-gray-400">{item.date ? new Date(item.date + 'T00:00').toLocaleDateString('fr-FR') : '-'}</p>
                                            </div>
                                        </div>
                                        <div className="relative">
                                            <button onClick={() => setOpenDrop(openDrop === dropKey ? null : dropKey)}
                                                className={`flex items-center gap-2 ${sCfg.bg} ${sCfg.border} border rounded-xl px-3.5 py-2 text-xs font-semibold shadow-sm hover:shadow-md transition-all min-w-[140px]`}>
                                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${sCfg.dot}`} />
                                                <span className={`${sCfg.text} flex-1 text-left`}>{sCfg.label}</span>
                                                <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${openDrop === dropKey ? 'rotate-180' : ''}`} />
                                            </button>
                                            {openDrop === dropKey && (
                                                <>
                                                    <div className="fixed inset-0 z-30" onClick={() => setOpenDrop(null)} />
                                                    <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl shadow-lg border border-gray-100 py-1.5 z-40 min-w-[170px]">
                                                        {STATUT_OPTIONS.map(o => (
                                                            <button key={o.value} onClick={() => { setOpenDrop(null); handleStatutChange(item.client_id, item.type, o.value); }}
                                                                className={`w-full text-left px-4 py-2.5 text-xs flex items-center gap-3 transition-colors ${currentStatut === o.value ? 'bg-gray-50 font-bold' : 'hover:bg-gray-50'}`}>
                                                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${o.dot}`} />
                                                                <span className="text-gray-700 flex-1">{o.label}</span>
                                                                {currentStatut === o.value && <span className="text-[#1B365D]">&#10003;</span>}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}
